import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminService } from "../../features/admin/service";
import type { AdminMarketReviewNotification } from "../../features/admin/types";

/** Single admin bell combining every admin-facing notification stream into
 * one tabbed dropdown, replacing what used to be two separate bell icons
 * (AdminMarketReviewBell + AdminNotificationBell) sitting side by side.
 *
 * Each tab is one distinct kind of thing an admin needs to act on — not just
 * "Market Review" vs. a single catch-all "Alerts" bucket, which used to mix
 * verification requests, duplicate-deal flags, gallery moderation, flagged
 * deals and geo-alias issues into one undifferentiated list. Alert types are
 * grouped into their own tab via ALERT_GROUPS below; anything not covered by
 * a group lands in "Other" rather than being silently dropped. */

interface AdminLogItem {
  _id: string;
  type: string;
  title: string;
  body: string;
  metadata?: Record<string, any>;
  source_module?: string;
  created_at: string;
}

const RECENT_WINDOW_MS = 24 * 60 * 60 * 1000;
const ALERTS_FETCH_SIZE = 100;

// Each alert `type` (see record_admin_notification_log call sites across the
// backend) is bucketed into one tab. A type not listed here falls into the
// "other" tab rather than vanishing.
const ALERT_GROUPS: { key: string; label: string; types: string[] }[] = [
  {
    key: "verification",
    label: "Verification",
    types: ["seller_pending_verification", "buyer_pending_verification", "verification_requested"],
  },
  {
    key: "flagged_deals",
    label: "Flagged Deals",
    types: ["last_minute_deal.pending_review", "cancelled_deal.pending_review", "date_expiry.pending_review"],
  },
  {
    key: "gallery_review",
    label: "Gallery Review",
    types: ["gallery.pending_review", "preview_pages.pending_review"],
  },
  {
    key: "duplicates",
    label: "Duplicate Deals",
    types: ["deal_possible_duplicate"],
  },
  {
    key: "geo_alias",
    label: "Geo Alias",
    types: ["geo_alias_unresolved"],
  },
  {
    // Its own tab rather than folded into Verification: this is an account
    // recovery/support action (an admin edits the user's locked login number),
    // not an approval of the account itself.
    key: "number_change",
    label: "Number Change",
    types: ["phone_change_requested"],
  },
  {
    key: "payments",
    label: "Payments",
    types: ["payment.gateway_incident"],
  },
];
const OTHER_GROUP = { key: "other", label: "Other" };
const ALL_ALERT_GROUPS = [...ALERT_GROUPS, OTHER_GROUP];

const TYPE_TO_GROUP: Record<string, string> = {};
for (const g of ALERT_GROUPS) for (const t of g.types) TYPE_TO_GROUP[t] = g.key;
const groupForType = (type: string) => TYPE_TO_GROUP[type] ?? OTHER_GROUP.key;

// A few tabs above still bundle more than one exact `type` (Verification has
// 3, Flagged Deals has 3, Gallery Review has 2) — a small sub-type badge on
// each item tells those apart without needing a tab per exact type.
const TYPE_LABELS: Record<string, string> = {
  seller_pending_verification: "Seller Onboarding",
  buyer_pending_verification: "Buyer Onboarding",
  verification_requested: "Self-Requested",
  "last_minute_deal.pending_review": "Last-Minute Deal",
  "cancelled_deal.pending_review": "Cancelled Deal",
  "date_expiry.pending_review": "Dates Expired",
  "gallery.pending_review": "Gallery",
  "preview_pages.pending_review": "Preview Pages",
  phone_change_requested: "Number Change",
  "payment.gateway_incident": "Gateway Incident",
};

type Tab = "market_review" | (typeof ALL_ALERT_GROUPS)[number]["key"];

const AdminNotificationsCenter: React.FC = () => {
  const [marketItems, setMarketItems] = useState<AdminMarketReviewNotification[]>([]);
  const [alertItems, setAlertItems] = useState<AdminLogItem[]>([]);
  const [tab, setTab] = useState<Tab>("market_review");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const loadMarket = () => {
    adminService.getPendingMarketReviewNotifications()
      .then((res) => setMarketItems(res.notifications))
      .catch(() => {});
  };
  const loadAlerts = () => {
    adminService.getAdminNotificationsLog({ page: 1, page_size: ALERTS_FETCH_SIZE })
      .then((res) => setAlertItems(res.items))
      .catch(() => {});
  };

  useEffect(() => {
    loadMarket();
    loadAlerts();
    const interval = setInterval(() => {
      loadMarket();
      loadAlerts();
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const alertsByGroup = useMemo(() => {
    const map: Record<string, AdminLogItem[]> = {};
    for (const g of ALL_ALERT_GROUPS) map[g.key] = [];
    for (const item of alertItems) map[groupForType(item.type)].push(item);
    return map;
  }, [alertItems]);

  const recentAlertsCount = alertItems.filter(
    (n) => Date.now() - new Date(n.created_at).getTime() < RECENT_WINDOW_MS,
  ).length;
  const totalCount = marketItems.length + recentAlertsCount;

  const goToSeller = async (n: AdminMarketReviewNotification) => {
    try { await adminService.markNotificationRead(n.id); } catch { /* non-fatal */ }
    setMarketItems((prev) => prev.filter((x) => x.id !== n.id));
    setOpen(false);
    // Deep-link straight to the seller's Market Map modal (the actual
    // approve/reject review UI, opened via ?market_user= — see the
    // deep-link effect in AdminUsersPage) rather than a fuzzy name/email
    // search that just lands on the user list.
    if (n.metadata?.seller_id) {
      const params = new URLSearchParams({ market_user: n.metadata.seller_id });
      if (n.metadata?.seller_name) params.set("market_user_name", n.metadata.seller_name);
      navigate(`/admin/users?${params.toString()}`);
      return;
    }
    const searchTerm = n.metadata?.seller_email || n.metadata?.seller_name;
    navigate(searchTerm ? `/admin/users?search=${encodeURIComponent(searchTerm)}` : "/admin/users");
  };

  const goToUser = (n: AdminLogItem) => {
    setOpen(false);
    // "deal_possible_duplicate" (see _notify_admins_possible_duplicate in
    // backend/app/modules/deals/service.py) is the one alert type that's
    // about a deal, not a user — deep-link to it instead.
    if (n.type === "deal_possible_duplicate" && n.metadata?.deal_id) {
      navigate(`/admin/deals?deal_id=${encodeURIComponent(n.metadata.deal_id)}`);
      return;
    }
    // Gallery/preview-page moderation alerts (see notify_gallery_pending_review
    // in backend/app/modules/deals/gallery_moderation.py) deep-link straight
    // to the gallery review page for the deal in question.
    if (
      (n.type === "gallery.pending_review" || n.type === "preview_pages.pending_review") &&
      n.metadata?.deal_id && n.metadata?.collection
    ) {
      navigate(`/admin/deals/gallery-review?collection=${encodeURIComponent(n.metadata.collection)}&deal_id=${encodeURIComponent(n.metadata.deal_id)}`);
      return;
    }
    // Last-minute-deal / cancelled-deal pending-review alerts (see
    // notify_last_minute_pending_review in
    // backend/app/modules/deals/typed/last_minute_review.py and
    // notify_cancelled_pending_review in
    // backend/app/modules/deals/typed/cancelled_deal_review.py) — both land on
    // the same combined Flagged Deals Review page, deep-linked to the row.
    if (
      (n.type === "last_minute_deal.pending_review" || n.type === "cancelled_deal.pending_review") &&
      n.metadata?.deal_id
    ) {
      navigate(`/admin/deals/last-minute-review?deal_id=${encodeURIComponent(n.metadata.deal_id)}`);
      return;
    }
    // Unresolved-geo-alias alert (see _notify_admins_unresolved_geo_alias in
    // backend/app/modules/deals/typed/service.py) — same landing spot as
    // Market Review notifications, since it's the same Market Map modal the
    // admin needs to inspect the failing destination on.
    if (n.type === "geo_alias_unresolved" && n.metadata?.user_id) {
      const params = new URLSearchParams({ market_user: n.metadata.user_id });
      if (n.metadata?.seller_name) params.set("market_user_name", n.metadata.seller_name);
      navigate(`/admin/users?${params.toString()}`);
      return;
    }
    // Pending-verification alerts — onboarding completion (see
    // _notify_admins_pending_verification in backend/app/modules/auth/onboarding.py,
    // types "seller_pending_verification"/"buyer_pending_verification") and the
    // self-serve request (see request_verification in
    // backend/app/modules/users/service.py, type "verification_requested") —
    // all deep-link to the same Onboarding Data modal with its Verify action.
    if (
      (n.type === "seller_pending_verification" || n.type === "buyer_pending_verification" || n.type === "verification_requested") &&
      n.metadata?.user_id
    ) {
      navigate(`/admin/users?onboarding_user=${encodeURIComponent(n.metadata.user_id)}`);
      return;
    }
    // Account-number change request (see request_number_change in
    // backend/app/modules/users/service.py) — opens the Change Number modal on
    // the admin Users page, pre-filled from the user's pending request.
    if (n.type === "phone_change_requested" && n.metadata?.user_id) {
      navigate(`/admin/users?number_change_user=${encodeURIComponent(n.metadata.user_id)}`);
      return;
    }
    // Dates-expired pending-review alert (see notify_date_expiry_pending_review
    // in backend/app/modules/deals/typed/date_expiry_review.py) — this reason
    // doesn't have its own review page (it's a chip filter on AdminDealsPage),
    // but ?deal_id= there opens the specific deal's detail modal directly
    // regardless of which filter is active, so it still deep-links precisely.
    if (n.type === "date_expiry.pending_review" && n.metadata?.deal_id) {
      navigate(`/admin/deals?deal_id=${encodeURIComponent(n.metadata.deal_id)}`);
      return;
    }
    // Different alert types key their subject under different metadata
    // names (seller_* for market-review alerts, user_* for everything else) —
    // check both so every alert type can deep-link to the right user.
    const searchTerm =
      n.metadata?.user_email || n.metadata?.user_name ||
      n.metadata?.seller_email || n.metadata?.seller_name ||
      n.metadata?.buyer_email || n.metadata?.buyer_name;
    navigate(searchTerm ? `/admin/users?search=${encodeURIComponent(searchTerm)}` : "/admin/users");
  };

  const activeAlerts = tab === "market_review" ? [] : alertsByGroup[tab] ?? [];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
        title="Admin notifications"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {totalCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-orange-500 text-white text-[10px] font-bold ring-2 ring-gray-900">
            {totalCount > 9 ? "9+" : totalCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[26rem] bg-gray-900 border border-gray-800 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="flex overflow-x-auto border-b border-gray-800 no-scrollbar">
            <button
              type="button"
              onClick={() => setTab("market_review")}
              className={`shrink-0 px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-colors relative ${
                tab === "market_review" ? "text-white" : "text-gray-500 hover:text-gray-300"
              }`}
            >
              Market Review
              {marketItems.length > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-orange-500/20 text-orange-400 text-[10px] font-bold">
                  {marketItems.length}
                </span>
              )}
              {tab === "market_review" && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500" />}
            </button>
            {ALL_ALERT_GROUPS.map((g) => {
              const count = alertsByGroup[g.key]?.length ?? 0;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => setTab(g.key)}
                  className={`shrink-0 px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-colors relative ${
                    tab === g.key ? "text-white" : "text-gray-500 hover:text-gray-300"
                  }`}
                >
                  {g.label}
                  {count > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-indigo-500/20 text-indigo-400 text-[10px] font-bold">
                      {count}
                    </span>
                  )}
                  {tab === g.key && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500" />}
                </button>
              );
            })}
          </div>

          {tab === "market_review" ? (
            <>
              <div className="px-4 py-2.5 border-b border-gray-800/60">
                <p className="text-xs text-gray-500 leading-snug">Sellers who added new markets after onboarding — their deals there are blocked until you verify.</p>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {marketItems.length === 0 ? (
                  <div className="px-4 py-6 text-center text-xs text-gray-500">Nothing pending.</div>
                ) : (
                  marketItems.map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => goToSeller(n)}
                      className="w-full text-left px-4 py-3 border-b border-gray-800/60 last:border-b-0 hover:bg-gray-800/60 transition-colors"
                    >
                      <p className="text-sm text-white">{n.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5 leading-snug">{n.body}</p>
                    </button>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {activeAlerts.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-gray-500">Nothing here.</div>
              ) : (
                activeAlerts.map((n) => (
                  <button
                    key={n._id}
                    type="button"
                    onClick={() => goToUser(n)}
                    className="w-full text-left px-4 py-3 border-b border-gray-800/60 last:border-b-0 hover:bg-gray-800/60 transition-colors"
                  >
                    {TYPE_LABELS[n.type] && (
                      <span className="inline-block mb-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-gray-800 text-gray-400">
                        {TYPE_LABELS[n.type]}
                      </span>
                    )}
                    <p className="text-sm text-white">{n.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5 leading-snug">{n.body}</p>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminNotificationsCenter;
